import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBusinessDetailedView } from "../../../redux/slices/BusinessProfileSlice";
import ProfileSectionLayout from "../../../components/ProfileSectionLayout/ProfileSectionLayout";
import {
    capitalizeWords, formatDate
} from "../../../helpers/helpers";
import CustomModal from "../../../components/CustomModal/CustomModal";
import EditDropdownsForm from "./EditDropdownsForm";
import EditInputForm from "./EditInputForm";
import ProfileDetail from "../../../components/Details/ProfileDetail";
import ReferenceLinksProfileView from "../../../components/Details/ReferenceLinksProfileView";
import { GLOBAL_CONSTANTS, TITLE_TXT } from "../../../constants/constants";
import {
    isInvoiceStyleCategory,
    isVenturesCategory,
} from "../../../helpers/dealCategoryProducts";

const CompanyDetails = () => {
    const { user } = useSelector((state) => state.auth);
    const { busDetailedViewData, loading } = useSelector((state) => state.profile1);
    const { activeCategory } = useSelector((state) => state.dealCategory || {});
    const dispatch = useDispatch();
    const [editDropdownsModalOpen, setEditDropdownsModalOpen] = useState(false);
    const [isIndustryType, setIsIndustryType] = useState(false);
    const [isAnnualTurnover, setIsAnnualTurnover] = useState(false);
    const [isMultipleGstin, setIsMultipleGstin] = useState(false);

    const [editInputModelOpen, setEditInputModelOpen] = useState(false);
    const [isWebsite, setIsWebsite] = useState(false);
    const [isBusDescription, setIsBusDescription] = useState(false);
    const [isFinancialSummary, setIsFinancialSummary] = useState(false);

    const dealCategoryId = activeCategory?.dealCategoryId;
    const showCreditFields = isInvoiceStyleCategory(dealCategoryId);
    const showAmountToRaise = isVenturesCategory(dealCategoryId);
    const showIfScfNarrativeFields = showCreditFields;
    const showReferenceLinks = showAmountToRaise;

    useEffect(() => {
        dispatch(
            getBusinessDetailedView(
                dealCategoryId ? { dealCategoryId } : undefined
            )
        );
    }, [dispatch, dealCategoryId]);

    const openEditDropdownModal = (field) => {
        setEditDropdownsModalOpen(true);
        setIsIndustryType(field === "industryType");
        setIsAnnualTurnover(field === "annualTurnover");
        setIsMultipleGstin(field === "hasMultipleGstin");
    };

    const openEditInputModal = (field) => {
        setEditInputModelOpen(true);
        setIsWebsite(field === "website");
        setIsBusDescription(field === "busDescription");
        setIsFinancialSummary(field === "financialSummary");
    };

    return (
        <>
            <ProfileSectionLayout loading={loading} title={TITLE_TXT.COMP_DETS}>
                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.COMP_NAME}
                            value={
                                <span style={{ fontWeight: 700 }}>
                                    {busDetailedViewData?.busDetails?.busName}
                                </span>
                            }
                            isVerified={busDetailedViewData?.kycDetails?.kycDirectorsPanValue?.isVerified}
                        />

                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.WEBSITE}
                            value={busDetailedViewData?.busDetails?.website || GLOBAL_CONSTANTS.NA}
                            isEditable
                            fieldKey="website"
                            type="input"
                            orgId={user?.orgId}
                            onEdit={() => openEditInputModal("website")}

                        />

                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.IND_TYPE}
                            value={capitalizeWords(busDetailedViewData?.busDetails?.industryTypeName) || GLOBAL_CONSTANTS.NA}
                            isEditable
                            onEdit={() => openEditDropdownModal("industryType")}
                        />

                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.COMP_TYPE}
                            value={capitalizeWords(busDetailedViewData?.busDetails?.businessTypeName) || GLOBAL_CONSTANTS.NA}
                        />

                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.ANN_TURN}
                            value={capitalizeWords(busDetailedViewData?.busDetails?.annualTurnoverName) || GLOBAL_CONSTANTS.NA}
                            isEditable
                            onEdit={() => openEditDropdownModal("annualTurnover")}
                        />

                        {showCreditFields && (
                            <>
                                <ProfileDetail
                                    label={GLOBAL_CONSTANTS.CREDIT_TENURE}
                                    value={busDetailedViewData?.busDetails?.creditTenure || GLOBAL_CONSTANTS.NA}
                                />

                                <ProfileDetail
                                    label={GLOBAL_CONSTANTS.CREDIT_AMT}
                                    value={
                                        busDetailedViewData?.busDetails?.requestedCreditAmt ||
                                        busDetailedViewData?.busDetails?.expectedCredit ||
                                        busDetailedViewData?.expectedCredit ||
                                        busDetailedViewData?.busDetails?.amountToRaise ||
                                        GLOBAL_CONSTANTS.NA
                                    }
                                />
                            </>
                        )}

                        {showAmountToRaise && (
                            <ProfileDetail
                                label={GLOBAL_CONSTANTS.AMT_TO_RAISE}
                                value={busDetailedViewData?.busDetails?.requestedCreditAmt || GLOBAL_CONSTANTS.NA}
                            />
                        )}

                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.INC_DATE}
                            value={formatDate(busDetailedViewData?.busDetails?.incorporationDate) || GLOBAL_CONSTANTS.NA}
                        />

                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.MULTIPLE_GST}
                            value={busDetailedViewData?.busDetails?.hasMultipleGstin ? "Yes" : "No"}
                            isEditable
                            fieldKey="hasMultipleGstin"
                            type="switch"
                            orgId={user?.orgId}
                            onEdit={() => openEditDropdownModal("hasMultipleGstin")}

                        />

                        {showIfScfNarrativeFields && (
                            <>
                                <ProfileDetail
                                    label={GLOBAL_CONSTANTS.BUS_DESC}
                                    value={busDetailedViewData?.busDetails?.busDescription || GLOBAL_CONSTANTS.NA}
                                    isEditable
                                    fieldKey="businessDescription"
                                    type="textarea"
                                    orgId={user?.orgId}
                                    onEdit={() => openEditInputModal("busDescription")}
                                />

                                <ProfileDetail
                                    label={GLOBAL_CONSTANTS.FIN_SUMMARY}
                                    value={busDetailedViewData?.busDetails?.financialSummary || GLOBAL_CONSTANTS.NA}
                                    isEditable
                                    fieldKey="financialSummary"
                                    type="textarea"
                                    orgId={user?.orgId}
                                    onEdit={() => openEditInputModal("financialSummary")}
                                />
                            </>
                        )}
                        <ProfileDetail
                            label={GLOBAL_CONSTANTS.COMP_REG_ADD}
                            value={
                                [
                                    busDetailedViewData?.billingAdd?.address1,
                                    busDetailedViewData?.billingAdd?.address2,
                                    busDetailedViewData?.billingAdd?.city,
                                    busDetailedViewData?.billingAdd?.state,
                                    busDetailedViewData?.billingAdd?.country,
                                    busDetailedViewData?.billingAdd?.pinCode,
                                ].filter(Boolean).join(", ") || GLOBAL_CONSTANTS.NA
                            }
                        />
                        {showReferenceLinks && (
                            <ReferenceLinksProfileView
                                referenceLinks={busDetailedViewData?.busDetails?.referenceLinks}
                            />
                        )}
            </ProfileSectionLayout>

            {editDropdownsModalOpen && (
                        <CustomModal
                            open={editDropdownsModalOpen}
                            centered
                            maskClosable={false}
                            onCancel={() => {
                                setEditDropdownsModalOpen(false);
                                setIsAnnualTurnover(false);
                                setIsIndustryType(false);
                                setIsMultipleGstin(false);
                            }}
                            content={
                                <EditDropdownsForm
                                    industry={busDetailedViewData?.busDetails?.industryTypeId}
                                    annualTurnover={busDetailedViewData?.busDetails?.annualTurnoverId}
                                    hasMultipleGstin={busDetailedViewData?.busDetails?.hasMultipleGstin}
                                    isIndustry={isIndustryType}
                                    isAnnualTurnover={isAnnualTurnover}
                                    isMultipleGstin={isMultipleGstin}
                                    setEditDropdownsModalOpen={setEditDropdownsModalOpen}
                                    setIsAnnualTurnover={setIsAnnualTurnover}
                                    setIsMultipleGstin={setIsMultipleGstin}
                                    setIsIndustryType={setIsIndustryType}
                                />
                            }
                        />
            )}

            {editInputModelOpen && (
                        <CustomModal
                            open={editInputModelOpen}
                            centered
                            maskClosable={false}
                            onCancel={() => {
                                setEditInputModelOpen(false);
                                setIsWebsite(false);
                                setIsBusDescription(false);
                                setIsFinancialSummary(false);
                            }}
                            content={
                                <EditInputForm
                                    website={busDetailedViewData?.busDetails?.website}
                                    busDescription={busDetailedViewData?.busDetails?.busDescription}
                                    financialSummary={busDetailedViewData?.busDetails?.financialSummary}
                                    isWebsite={isWebsite}
                                    isBusDescription={isBusDescription}
                                    isFinancialSummary={isFinancialSummary}
                                    setEditInputModelOpen={setEditInputModelOpen}
                                    setIsWebsite={setIsWebsite}
                                    setIsBusDescription={setIsBusDescription}
                                    setIsFinancialSummary={setIsFinancialSummary}
                                />
                            }
                        />
            )}
        </>
    );
};

export default CompanyDetails;
